import * as React from "react";
import { cn } from "@/lib/utils";
import { motion } from "framer-motion";
import { Database } from "lucide-react";

// ─────────────────────────────────────────────────────────────
// Base Table Components
// ─────────────────────────────────────────────────────────────

const Table = React.forwardRef(({ className, ...props }, ref) => (
    <div className="relative w-full overflow-auto">
        <table
            ref={ref}
            className={cn("w-full caption-bottom text-sm", className)}
            {...props}
        />
    </div>
));
Table.displayName = "Table";

const TableHeader = React.forwardRef(({ className, ...props }, ref) => (
    <thead ref={ref} className={cn("[&_tr]:border-b", className)} {...props} />
));
TableHeader.displayName = "TableHeader";

const TableBody = React.forwardRef(({ className, ...props }, ref) => (
    <tbody
        ref={ref}
        className={cn("[&_tr:last-child]:border-0", className)}
        {...props}
    />
));
TableBody.displayName = "TableBody";

const TableFooter = React.forwardRef(({ className, ...props }, ref) => (
    <tfoot
        ref={ref}
        className={cn(
            "border-t bg-muted/50 font-medium [&>tr]:last:border-b-0",
            className,
        )}
        {...props}
    />
));
TableFooter.displayName = "TableFooter";

const TableRow = React.forwardRef(({ className, ...props }, ref) => (
    <tr
        ref={ref}
        className={cn(
            "border-b transition-colors hover:bg-muted/50 data-[state=selected]:bg-muted",
            className,
        )}
        {...props}
    />
));
TableRow.displayName = "TableRow";

const TableHead = React.forwardRef(({ className, ...props }, ref) => (
    <th
        ref={ref}
        className={cn(
            "h-11 px-4 text-left align-middle text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400 whitespace-nowrap",
            className,
        )}
        {...props}
    />
));
TableHead.displayName = "TableHead";

const TableCell = React.forwardRef(({ className, ...props }, ref) => (
    <td
        ref={ref}
        className={cn("p-4 align-middle text-sm", className)}
        {...props}
    />
));
TableCell.displayName = "TableCell";

const TableCaption = React.forwardRef(({ className, ...props }, ref) => (
    <caption
        ref={ref}
        className={cn("mt-4 text-sm text-muted-foreground", className)}
        {...props}
    />
));
TableCaption.displayName = "TableCaption";

// ─────────────────────────────────────────────────────────────
// DataTable
// ─────────────────────────────────────────────────────────────
//
// Column sizing contract (per column config):
//   - nothing set             → column auto-sizes to its content, never clipped.
//   - minWidth only           → floor width; column still grows for longer content.
//   - maxWidth set             → ceiling width; content wraps to additional lines by
//                                default, or truncates with an ellipsis if
//                                `col.truncate: true` is also set.
//   - width set                → exact/target width, same overflow handling as
//                                maxWidth (wrap or truncate).
//
// This intentionally avoids `table-layout: fixed` + <colgroup>. Fixed layout
// pins every column to its declared width regardless of content, which is
// exactly what breaks the "no width → show full content" case. Auto layout
// lets unconstrained columns size naturally while width/minWidth/maxWidth on
// individual cells still constrain just those columns.

const DEFAULT_MIN_WIDTH_FOR_STICKY = 180;

function toCssSize(value) {
    if (value == null) return undefined;
    return typeof value === "number" ? `${value}px` : value;
}

/**
 * Parses a column width declaration into a px number, used only for the
 * sticky-offset math before ResizeObserver has produced a real measurement.
 * Percentage/other CSS units can't be used for that math (unresolvable
 * without layout), so they fall through to the fallback.
 */
function resolveDeclaredWidth(widthValue, fallback) {
    if (typeof widthValue === "number") return widthValue;

    if (typeof widthValue === "string") {
        const trimmed = widthValue.trim();
        const pxMatch = trimmed.match(/^(\d+(?:\.\d+)?)(px)?$/);
        if (pxMatch) return parseFloat(pxMatch[1]);
        return fallback;
    }

    return fallback;
}

const DataTable = ({
    data = [],
    columns = [],
    loading = false,

    htrClass,
    trClass,
    thClass,
    tdClass,

    ExpandedRow,

    emptyText = "No record found",
    isAplyingFilter = false,
    resetFilters = () => { },

    stickyColumns,

    Icon = Database,
    subText = "Try adjusting your search or filter criteria",

    boxClass = "",
    maxHeight,
    onScroll = () => { },
    disableStickyOnMobile = true,
    getRowKey = (row, index) => row?.id ?? index,
}) => {
    const isEmpty = !data?.length;
    const showTable = !isEmpty || loading;

    // ─────────────────────────────────────────────────────────────
    // Mobile detection — matchMedia only fires on breakpoint crossing,
    // unlike a raw `resize` listener which fires continuously during drag.
    // ─────────────────────────────────────────────────────────────

    const [isMobile, setIsMobile] = React.useState(() =>
        typeof window !== "undefined" ? window.innerWidth < 768 : false,
    );

    React.useEffect(() => {
        if (typeof window === "undefined" || !window.matchMedia) return;

        const mql = window.matchMedia("(max-width: 767px)");
        const handleChange = (e) => setIsMobile(e.matches);

        setIsMobile(mql.matches);
        mql.addEventListener("change", handleChange);
        return () => mql.removeEventListener("change", handleChange);
    }, []);

    const shouldApplySticky = !(disableStickyOnMobile && isMobile);

    // ─────────────────────────────────────────────────────────────
    // Dynamic sticky width measurement
    // Declared `width`/`minWidth` are only a fallback/first-paint guess.
    // Real offsets are derived from the actual rendered width of each
    // sticky header cell, via ResizeObserver, so offsets stay correct
    // regardless of content length, column visibility toggles, font
    // size, zoom, or responsive breakpoints — and are unaffected by
    // whether the column ends up auto-sized, min-, or max-constrained.
    // ─────────────────────────────────────────────────────────────

    const thRefs = React.useRef({});
    const [measuredWidths, setMeasuredWidths] = React.useState({});

    const setThRef = React.useCallback(
        (key) => (el) => {
            if (el) {
                thRefs.current[key] = el;
            } else {
                delete thRefs.current[key];
            }
        },
        [],
    );

    const stickyKeys = React.useMemo(
        () => columns.filter((col) => col.sticky).map((col) => col.key),
        [columns],
    );

    React.useLayoutEffect(() => {
        if (!shouldApplySticky || typeof ResizeObserver === "undefined") return;

        const observer = new ResizeObserver((entries) => {
            setMeasuredWidths((prev) => {
                let changed = false;
                const next = { ...prev };

                entries.forEach((entry) => {
                    const key = entry.target.dataset.colKey;
                    const width = entry.target.getBoundingClientRect().width;

                    if (key && Math.abs((next[key] || 0) - width) > 0.5) {
                        next[key] = width;
                        changed = true;
                    }
                });

                return changed ? next : prev;
            });
        });

        stickyKeys.forEach((key) => {
            const el = thRefs.current[key];
            if (el) observer.observe(el);
        });

        return () => observer.disconnect();
    }, [shouldApplySticky, stickyKeys, data]);

    // ─────────────────────────────────────────────────────────────
    // Alignment
    // ─────────────────────────────────────────────────────────────

    const getAlignClass = React.useCallback((align) => {
        if (align === "right") return "text-right";
        if (align === "center") return "text-center";
        return "text-left";
    }, []);

    // Prefers the measured DOM width; falls back to a *resolvable* declared
    // width/minWidth until the first ResizeObserver callback lands. This is
    // ONLY used for sticky-offset math, never for actually sizing the column
    // (that's handled by real CSS width/minWidth/maxWidth in getSizeStyles).
    const getColumnWidthForOffset = React.useCallback(
        (col) => {
            if (measuredWidths[col.key] != null) {
                return measuredWidths[col.key];
            }

            if (col.width != null) {
                return resolveDeclaredWidth(
                    col.width,
                    col.minWidth ?? DEFAULT_MIN_WIDTH_FOR_STICKY,
                );
            }

            if (typeof col.minWidth === "number") {
                return col.minWidth;
            }

            if (typeof col.maxWidth === "number") {
                return col.maxWidth;
            }

            return DEFAULT_MIN_WIDTH_FOR_STICKY;
        },
        [measuredWidths],
    );

    // ─────────────────────────────────────────────────────────────
    // Real column sizing — applied directly to <th>/<td>, no colgroup,
    // no table-layout: fixed. Auto layout does the rest:
    //   - width unset, minWidth unset, maxWidth unset → grows to content.
    //   - minWidth   → CSS min-width, floor only.
    //   - maxWidth   → CSS max-width, ceiling; pairs with getWrapClass.
    //   - width      → CSS width, treated as a target/ceiling; pairs with
    //                  getWrapClass the same way maxWidth does.
    // ─────────────────────────────────────────────────────────────

    const getSizeStyles = React.useCallback((col) => {
        const style = {};
        if (col.width != null) style.width = toCssSize(col.width);
        if (col.minWidth != null) style.minWidth = toCssSize(col.minWidth);
        if (col.maxWidth != null) style.maxWidth = toCssSize(col.maxWidth);
        return style;
    }, []);

    // Only columns with a width/maxWidth ceiling need overflow handling.
    // Everything else is left alone so it can show its full content.
    const getWrapClass = React.useCallback((col) => {
        const hasCeiling = col.width != null || col.maxWidth != null;
        if (!hasCeiling) return "";

        return col.truncate
            ? "block w-full overflow-hidden text-ellipsis whitespace-nowrap"
            : "block w-full whitespace-normal break-words";
    }, []);

    // ─────────────────────────────────────────────────────────────
    // Sticky offsets — computed ONCE per render as O(n) lookup arrays,
    // instead of an O(n) scan repeated for every single cell in every
    // row (which was effectively O(rows × cols²)).
    // ─────────────────────────────────────────────────────────────

    const stickyOffsets = React.useMemo(() => {
        const left = new Array(columns.length).fill(0);
        const right = new Array(columns.length).fill(0);

        let leftAcc = 0;
        for (let i = 0; i < columns.length; i++) {
            if (columns[i].sticky === "left") {
                left[i] = leftAcc;
                leftAcc += getColumnWidthForOffset(columns[i]);
            }
        }

        let rightAcc = 0;
        for (let i = columns.length - 1; i >= 0; i--) {
            if (columns[i].sticky === "right") {
                right[i] = rightAcc;
                rightAcc += getColumnWidthForOffset(columns[i]);
            }
        }

        return { left, right };
    }, [columns, getColumnWidthForOffset]);

    const getStickyStyles = React.useCallback(
        (col, index, isHeader = false) => {
            if (!col.sticky || !shouldApplySticky) return {};

            const commonStyles = {
                position: "sticky",
                zIndex: isHeader ? 40 : 30,
            };

            if (col.sticky === "left") {
                return { ...commonStyles, left: `${stickyOffsets.left[index]}px` };
            }

            if (col.sticky === "right") {
                return { ...commonStyles, right: `${stickyOffsets.right[index]}px` };
            }

            return {};
        },
        [stickyOffsets, shouldApplySticky],
    );

    return (
        <div
            className={cn(
                "relative w-full overflow-hidden rounded-xl border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900",
                boxClass,
            )}
        >
            {/* Horizontal Scroll */}
            <div
                className="relative w-full overflow-x-auto scrollbar-thin touch-pan-x"
                onScroll={onScroll}
            >
                {/* Vertical Scroll */}
                <div
                    style={{
                        maxHeight: maxHeight || undefined,
                        overflowY: maxHeight ? "auto" : undefined,
                    }}
                >
                    <table
                        className="border-collapse"
                        style={{
                            width: "max-content",
                            minWidth: "100%",
                            tableLayout: "auto",
                        }}
                    >
                        <thead>
                            <tr
                                className={cn(
                                    "border-b border-slate-200 bg-slate-50 dark:border-slate-800 dark:bg-slate-800/50",
                                    htrClass,
                                )}
                            >
                                {columns.map((col, index) => {
                                    const isLeftSticky = col.sticky === "left";
                                    const isRightSticky = col.sticky === "right";
                                    const alignClass = getAlignClass(col.align);
                                    const wrapClass = getWrapClass(col);

                                    return (
                                        <th
                                            key={col.key}
                                            scope="col"
                                            ref={col.sticky ? setThRef(col.key) : undefined}
                                            data-col-key={col.sticky ? col.key : undefined}
                                            className={cn(
                                                "px-2 md:px-4 py-2 md:py-3 text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400",
                                                alignClass,
                                                !wrapClass &&
                                                col.headerNoWrap !== false &&
                                                "whitespace-nowrap",
                                                shouldApplySticky &&
                                                col.sticky &&
                                                "bg-slate-50 dark:bg-slate-800",
                                                shouldApplySticky &&
                                                isLeftSticky &&
                                                "shadow-[1px_0_0_0_theme(colors.slate.200)] dark:shadow-[1px_0_0_0_theme(colors.slate.700)]",
                                                shouldApplySticky &&
                                                isRightSticky &&
                                                "shadow-[-1px_0_0_0_theme(colors.slate.200)] dark:shadow-[-1px_0_0_0_theme(colors.slate.700)]",
                                                col.thClass,
                                                thClass,
                                            )}
                                            style={{
                                                ...getSizeStyles(col),
                                                ...getStickyStyles(col, index, true),
                                            }}
                                        >
                                            {wrapClass ? (
                                                <span className={wrapClass}>
                                                    {typeof col.label === "function"
                                                        ? col.label()
                                                        : col.label}
                                                </span>
                                            ) : typeof col.label === "function" ? (
                                                col.label()
                                            ) : (
                                                col.label
                                            )}
                                        </th>
                                    );
                                })}
                            </tr>
                        </thead>

                        {showTable && (
                            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                                {data.map((row, rowIndex) => (
                                    <DataTableRow
                                        key={getRowKey(row, rowIndex)}
                                        row={row}
                                        rowIndex={rowIndex}
                                        columns={columns}
                                        trClass={trClass}
                                        tdClass={tdClass}
                                        shouldApplySticky={shouldApplySticky}
                                        getAlignClass={getAlignClass}
                                        getSizeStyles={getSizeStyles}
                                        getWrapClass={getWrapClass}
                                        getStickyStyles={getStickyStyles}
                                        ExpandedRow={ExpandedRow}
                                    />
                                ))}
                            </tbody>
                        )}
                    </table>
                </div>
            </div>

            {/* Empty State */}
            {isEmpty && !loading && (
                <div className="flex flex-col items-center justify-center px-6 py-10 text-center min-h-[160px]">
                    <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-slate-100 dark:bg-slate-800">
                        <Icon className="h-8 w-8 text-slate-400" />
                    </div>

                    <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
                        {emptyText}
                    </h3>

                    {isAplyingFilter && (
                        <>
                            <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
                                {subText}
                            </p>
                            <button
                                onClick={resetFilters}
                                className="mt-5 rounded-lg bg-teal-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-teal-700"
                            >
                                Clear Filters
                            </button>
                        </>
                    )}
                </div>
            )}

            {/* Loading State — scoped to this component, not the whole viewport */}
            {isEmpty && loading && (
                <div className="min-h-screen w-screen absolute top-0 left-0 flex items-center justify-center bg-[rgba(0,0,0,0.6)] z-50">
                    <div className="animate-spin rounded-full h-12 w-12 sm:h-16 sm:w-16 border-b-4 border-[#0072FF]"></div>
                </div>
            )}
        </div>
    );
};

// ─────────────────────────────────────────────────────────────
// Row — isolated and memoized so a re-render of the table
// (e.g. isMobile flipping, an unrelated state update) doesn't
// force every row to re-render when its own row data hasn't changed.
// ─────────────────────────────────────────────────────────────

const DataTableRow = React.memo(function DataTableRow({
    row,
    rowIndex,
    columns,
    trClass,
    tdClass,
    shouldApplySticky,
    getAlignClass,
    getSizeStyles,
    getWrapClass,
    getStickyStyles,
    ExpandedRow,
}) {
    return (
        <React.Fragment>
            <motion.tr
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.18, delay: rowIndex * 0.02 }}
                className={cn(
                    "transition-colors hover:bg-slate-50 dark:hover:bg-slate-800/40",
                    // trClass may be a callback so a row can style itself from its data
                    typeof trClass === "function" ? trClass(row, rowIndex) : trClass,
                )}
            >
                {columns.map((col, colIndex) => {
                    const isLeftSticky = col.sticky === "left";
                    const isRightSticky = col.sticky === "right";
                    const alignClass = getAlignClass(col.align);
                    const wrapClass = getWrapClass(col);
                    const cellContent = col.render
                        ? col.render(row, rowIndex)
                        : (row?.[col.key] ?? "-");

                    const cellTooltip =
                        typeof col.getTooltip === "function"
                            ? col.getTooltip(row, rowIndex)
                            : typeof cellContent === "string"
                                ? cellContent
                                : "";
                    const cellTooltipText =
                        cellTooltip != null && cellTooltip !== "" && cellTooltip !== "-"
                            ? String(cellTooltip)
                            : undefined;

                    return (
                        <td
                            key={col.key}
                            title={cellTooltipText}
                            className={cn(
                                "px-2 md:px-4 py-2 md:py-3 text-xs md:text-sm text-slate-700 dark:text-slate-300",
                                alignClass,
                                shouldApplySticky && col.sticky && "bg-white dark:bg-slate-900",
                                shouldApplySticky &&
                                isLeftSticky &&
                                "shadow-[1px_0_0_0_theme(colors.slate.200)] dark:shadow-[1px_0_0_0_theme(colors.slate.700)]",
                                shouldApplySticky &&
                                isRightSticky &&
                                "shadow-[-1px_0_0_0_theme(colors.slate.200)] dark:shadow-[-1px_0_0_0_theme(colors.slate.700)]",
                                col.tdClass,
                                tdClass,
                            )}
                            style={{
                                ...getSizeStyles(col),
                                ...getStickyStyles(col, colIndex, false),
                            }}
                        >
                            {wrapClass ? (
                                <div className={wrapClass}>{cellContent}</div>
                            ) : (
                                cellContent
                            )}
                        </td>
                    );
                })}
            </motion.tr>

            {ExpandedRow ? ExpandedRow(row, rowIndex) : null}
        </React.Fragment>
    );
});

export {
    Table,
    TableHeader,
    TableBody,
    TableFooter,
    TableHead,
    TableRow,
    TableCell,
    TableCaption,
    DataTable,
};